import React, { useEffect, useState } from 'react'
import { Avatar, Button, Modal, Row, Col, Spin, Typography } from 'antd'
import { ExclamationCircleFilled, UserOutlined } from '@ant-design/icons'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UserService } from '~/Services'
import { SsoSessionUser } from '~/Services/UserService.d'
import { consumeSwitchAccountFlag, markSwitchAccount } from '~/Utils/ssoSwitchAccount'
import { AuthLayout } from './AuthLayout'
import styles from './Login.module.less'

const { Text, Link } = Typography
const { confirm } = Modal

interface LoginLocationState {
  from?: { pathname: string }
}

type LoginView = 'default' | 'checking-sso' | 'active-sso'

/** Authorization Code + PKCE. SSO session is checked on user action, not on mount — checking on mount
 * would surface "You're still signed in" right after Logout Current App, making the logout look like
 * it silently failed even though it succeeded (the SSO cookie is kept by design). */
export const Login = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<LoginLocationState>()
  const [error, setError] = useState<string>()
  const [view, setView] = useState<LoginView>('default')
  const [ssoUser, setSsoUser] = useState<SsoSessionUser>()

  const resolveReturnTo = () => {
    const { from } = location.state || {}
    return from && from.pathname !== '/login' ? from.pathname : undefined
  }

  // Shared by the credential path and "Continue as X" — same authorize redirect either way. If the SSO
  // cookie is still valid, AuthServer issues the code without a login prompt.
  const beginAuth = async () => {
    setError(undefined)
    try {
      await UserService.Auth.beginLogin(resolveReturnTo())
    } catch (err) {
      // Web Crypto API unavailable (old browser / insecure context) — auth flow itself never started.
      setError(t('loginPage.error'))
    }
  }

  // "Use another account" already confirmed there's no session left (it just ended one) — jump straight
  // into the authorize redirect instead of making the user land on this page and click "Đăng nhập" again.
  // Safe to auto-redirect here specifically because the cookie is gone: AuthServer can only land on the
  // real credential form, never silently re-sign the user back in the way it would after Logout Current App.
  useEffect(() => {
    if (consumeSwitchAccountFlag()) {
      setView('checking-sso')
      beginAuth()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoginClick = async () => {
    setError(undefined)
    setView('checking-sso')

    let sessionUser: SsoSessionUser | undefined
    try {
      const res = await UserService.Auth.getSession()
      if (res.data.authenticated && res.data.user) {
        sessionUser = res.data.user
      }
    } catch (err) {
      // A failed session check must not block login — fall through to the normal OIDC redirect below.
    }

    if (sessionUser) {
      setSsoUser(sessionUser)
      setView('active-sso')
      return
    }

    setView('default')
    await beginAuth()
  }

  // Switching accounts ends the current central SSO session, not just this app's — confirm before
  // reusing the existing Logout All transport. Cancelling must leave that session untouched.
  const handleConfirmSwitchAccount = () => {
    markSwitchAccount()
    UserService.Auth.logoutRedirect()
    // logoutRedirect() starts a full-page navigation; the promise never resolves so antd keeps the
    // confirm button disabled/loading until the browser actually leaves the page, instead of allowing
    // a second click to fire this again during that gap.
    return new Promise<void>(() => {})
  }

  const handleSwitchAccountRequest = () => {
    confirm({
      title: t('loginPage.switchAccountConfirmTitle'),
      icon: <ExclamationCircleFilled />,
      content: t('loginPage.switchAccountConfirmDescription'),
      okText: t('loginPage.switchAccountConfirm'),
      cancelText: t('loginPage.switchAccountCancel'),
      onOk: handleConfirmSwitchAccount,
    })
  }

  const renderPanelBody = () => {
    if (view === 'checking-sso') {
      return (
        <Row justify="center" style={{ padding: '40px 0' }}>
          <Spin size="large" />
        </Row>
      )
    }

    if (view === 'active-sso' && ssoUser) {
      return (
        <>
          <div className={styles.header}>
            <Typography.Title level={4}>{t('loginPage.stillSignedInTitle')}</Typography.Title>
            <span>{t('loginPage.stillSignedInSubtitle')}</span>
          </div>
          <Row align="middle" style={{ marginBottom: 24 }}>
            <Avatar size={40} icon={<UserOutlined />} style={{ marginRight: 12 }} />
            <Col flex="auto">
              <div>
                <Text strong>{ssoUser.name}</Text>
              </div>
              <div>
                <Text type="secondary">{ssoUser.email}</Text>
              </div>
            </Col>
          </Row>
          <Row justify="center" style={{ marginBottom: 12 }}>
            <Button type="primary" size="large" block onClick={beginAuth}>
              {t('loginPage.continueAs', { name: ssoUser.name })}
            </Button>
          </Row>
          <Row justify="center">
            <Button size="large" block onClick={handleSwitchAccountRequest}>
              {t('loginPage.signOutAndUseAnother')}
            </Button>
          </Row>
          {error && (
            <Row justify="center" style={{ marginTop: 16 }}>
              <Text type="danger">{error}</Text>
            </Row>
          )}
        </>
      )
    }

    return (
      <>
        <div className={styles.header}>
          <Typography.Title level={4}>{t('loginPage.title')}</Typography.Title>
          <span>{t('loginPage.subtitle')}</span>
        </div>
        <Row justify="center" style={{ marginTop: 32, marginBottom: 16 }}>
          <Button type="primary" size="large" block onClick={handleLoginClick}>
            {t('loginPage.submit')}
          </Button>
        </Row>
        {error && (
          <Row justify="center">
            <Text type="danger">{error}</Text>
          </Row>
        )}
        <Row justify="center" style={{ marginTop: 24 }}>
          <span style={{ paddingRight: '8px' }}>{t('loginPage.noAccount')}</span>
          <Link onClick={() => history.push('/register')}>{t('loginPage.register')}</Link>
        </Row>
        <Row justify="center" style={{ marginTop: 8 }}>
          <Link onClick={() => history.push('/forgotpassword')}>{t('loginPage.forgotPassword')}</Link>
        </Row>
      </>
    )
  }

  return <AuthLayout>{renderPanelBody()}</AuthLayout>
}
